import { PanelWindow, WindowSettings } from "@dextall/panel-hooks-editor-2d";
import { isAlmostEqualToZero, isAlmostEqual } from "@dextall/shared";
import { WindowCellSource, InsulatedGlassUnit } from "@dextall/windows-builder";
import { getWindowOverlapsSetting, type SystemSettings } from "../../../responses/systemSettings";
import type { CladdingCellStyle, IPanelSourceOpening } from "../../../responses/panelSource";

export const createWindowSourceDefinition = (
    panelOrigin: THREE.Vector3,
    panelWindowDefinition: IPanelSourceOpening,
    systemSetting: SystemSettings,
): PanelWindow => {
    const windowFrameOffset = systemSetting.windowFrameOffset;
    const sourceDefinitionBoxWidth = panelWindowDefinition.box.max.x - panelWindowDefinition.box.min.x;
    const sourceDefinitionBoxHeight = panelWindowDefinition.box.max.y - panelWindowDefinition.box.min.y;

    let windowWidth = 0;
    let windowHeight = 0;
    const cells: WindowCellSource[] = [];

    for (const cell of panelWindowDefinition.cells) {
        const offsetX = isAlmostEqualToZero(cell.offset.x) ? 0 : cell.offset.x - windowFrameOffset.left;

        const offsetY = isAlmostEqualToZero(cell.offset.y) ? 0 : cell.offset.y - windowFrameOffset.bottom;

        const width =
            cell.width -
            (isAlmostEqualToZero(cell.offset.x) ? windowFrameOffset.left : 0) -
            (isAlmostEqual(cell.offset.x + cell.width, sourceDefinitionBoxWidth) ? windowFrameOffset.right : 0);

        const height =
            cell.height -
            (isAlmostEqualToZero(cell.offset.y) ? windowFrameOffset.bottom : 0) -
            (isAlmostEqual(cell.offset.y + cell.height, sourceDefinitionBoxHeight) ? windowFrameOffset.top : 0);

        cells.push({
            offset: {
                x: convertToMetric(offsetX),
                y: convertToMetric(offsetY),
            },
            width: convertToMetric(width),
            height: convertToMetric(height),
            cellColor: cell.cellColor,
            cellStyle: cell.cellStyle,
            handlePosition: cell.handlePosition,
        });

        windowWidth = Math.max(windowWidth, offsetX + width);
        windowHeight = Math.max(windowHeight, offsetY + height);
    }

    return {
        definition: {
            cells,
            width: convertToMetric(windowWidth),
            height: convertToMetric(windowHeight),
            insideColor: panelWindowDefinition.insideColor,
            outsideColor: panelWindowDefinition.outsideColor,
            windowSillColor: panelWindowDefinition.windowSillColor,
            profileStyle: panelWindowDefinition.profileStyle,
            insulatedGlassUnit: glassUnitPlaceholder,
        },
        position: {
            x: convertToMetric(panelWindowDefinition.box.min.x - panelOrigin.x + windowFrameOffset.left),
            y: convertToMetric(panelWindowDefinition.box.min.y - panelOrigin.y + windowFrameOffset.bottom),
        },
    };
};

export const createEditor2DSystemSettings = (
    systemSetting: SystemSettings,
    panelStyle: CladdingCellStyle,
): WindowSettings => {
    const { windowFrameOffset } = systemSetting;
    const windowOverlaps = getWindowOverlapsSetting(systemSetting, panelStyle);
    return {
        overlappingSettings: {
            left: convertToMetric(windowOverlaps.left),
            right: convertToMetric(windowOverlaps.right),
            top: convertToMetric(windowOverlaps.top),
            bottom: convertToMetric(windowOverlaps.bottom),
        },
        frameOffset: {
            left: convertToMetric(windowFrameOffset.left),
            right: convertToMetric(windowFrameOffset.right),
            top: convertToMetric(windowFrameOffset.top),
            bottom: convertToMetric(windowFrameOffset.bottom),
        },
    };
};

const convertToMetric = (value: number) => Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, value);
const glassUnitPlaceholder: InsulatedGlassUnit = {
    id: "",
    name: "",
    category: "",
    profileType: 0,
};
